import React from 'react';

interface KnowledgeLibraryBulkSelectionToolbarProps {
	selectedCount: number;
	allSelectedAreFavorites: boolean;
	noneSelectedAreFavorites: boolean;
	canModifySelectedKnowledgeBooks: boolean;
	canManageSelectedKnowledgeBooks: boolean;
	onClearSelection: () => void;
	onAssignTags: () => void | Promise<void>;
	onAddToFavorites: () => void | Promise<void>;
	onRemoveFromFavorites: () => void | Promise<void>;
	onDeleteKnowledgeBooks: () => void | Promise<void>;
	className?: string;
}

const KnowledgeLibraryBulkSelectionToolbar: React.FC<KnowledgeLibraryBulkSelectionToolbarProps> = ({
	selectedCount,
	allSelectedAreFavorites,
	noneSelectedAreFavorites,
	canModifySelectedKnowledgeBooks,
	canManageSelectedKnowledgeBooks,
	onClearSelection,
	onAssignTags,
	onAddToFavorites,
	onRemoveFromFavorites,
	onDeleteKnowledgeBooks,
	className,
}) => {
	const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
	const overflowMenuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
		if (selectedCount === 0 && isOverflowMenuOpen) {
			setIsOverflowMenuOpen(false);
		}
	}, [selectedCount, isOverflowMenuOpen]);

	React.useEffect(() => {
		if (!isOverflowMenuOpen) {
			return;
		}

		const handlePointerDown = (event: MouseEvent) => {
			if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node)) {
				setIsOverflowMenuOpen(false);
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [isOverflowMenuOpen]);

	const handleAssignTagsClick = React.useCallback(() => {
		setIsOverflowMenuOpen(false);
		onAssignTags();
	}, [onAssignTags]);

	const handleAddToFavoritesClick = React.useCallback(() => {
		setIsOverflowMenuOpen(false);
		onAddToFavorites();
	}, [onAddToFavorites]);

	const handleRemoveFromFavoritesClick = React.useCallback(() => {
		setIsOverflowMenuOpen(false);
		onRemoveFromFavorites();
	}, [onRemoveFromFavorites]);

	const handleDeleteClick = React.useCallback(() => {
		setIsOverflowMenuOpen(false);
		onDeleteKnowledgeBooks();
	}, [onDeleteKnowledgeBooks]);

	if (selectedCount === 0) {
		return null;
	}

	const rootClassName = className
		? `knowledge-library-selection-toolbar ${className}`
		: 'knowledge-library-selection-toolbar';

	return (
		<div className={rootClassName}>
			<button
				type="button"
				className="knowledge-library-selection-clear-button"
				onClick={onClearSelection}
				title={InovxLoader.t('facility:knowledgeBook.clearSelection')}
				aria-label={InovxLoader.t('facility:knowledgeBook.clearSelection')}
			>
				<span className="fa fa-check-square-o" aria-hidden="true"></span>
			</button>
			<span className="knowledge-library-selection-count">
				{InovxLoader.t('facility:knowledgeBook.selectedCount', { count: selectedCount })}
			</span>
			<div className="knowledge-library-selection-toolbar-spacer" />
			<div className="knowledge-library-overflow-menu" ref={overflowMenuRef}>
				<button
					type="button"
					className="knowledge-library-overflow-trigger"
					title={InovxLoader.t('facility:knowledgeBook.selectionActions')}
					aria-label={InovxLoader.t('facility:knowledgeBook.selectionActions')}
					aria-haspopup="true"
					aria-expanded={isOverflowMenuOpen}
					onClick={() => setIsOverflowMenuOpen(open => !open)}
				>
					<span className="fa fa-bars" aria-hidden="true"></span>
				</button>
				{isOverflowMenuOpen && (
					<div className="knowledge-library-overflow-list" role="menu">
						{canModifySelectedKnowledgeBooks && (<button
							type="button"
							className="knowledge-library-overflow-item"
							role="menuitem"
							onClick={handleAssignTagsClick}
						>
							<span className="fa fa-tag" aria-hidden="true"></span>
							{InovxLoader.t('facility:knowledgeBook.assignTag')}
						</button>)}
						<button
							type="button"
							className="knowledge-library-overflow-item"
							role="menuitem"
							disabled={allSelectedAreFavorites}
							onClick={handleAddToFavoritesClick}
						>
							<span className="fa fa-star" aria-hidden="true"></span>
							{InovxLoader.t('facility:knowledgeBook.addToFavorites')}
						</button>
						<button
							type="button"
							className="knowledge-library-overflow-item"
							role="menuitem"
							disabled={noneSelectedAreFavorites}
							onClick={handleRemoveFromFavoritesClick}
						>
							<span className="fa fa-star-o" aria-hidden="true"></span>
							{InovxLoader.t('facility:knowledgeBook.removeFromFavorites')}
						</button>
						{canManageSelectedKnowledgeBooks && (<><div className="knowledge-library-overflow-separator" />
						<button
							type="button"
							className="knowledge-library-overflow-item"
							role="menuitem"
							onClick={handleDeleteClick}
						>
							<span className="fa fa-trash-o" aria-hidden="true"></span>
							{InovxLoader.t('facility:knowledgeBook.delete')}
						</button></>)}
					</div>
				)}
			</div>
		</div>
	);
};

export default KnowledgeLibraryBulkSelectionToolbar;
